import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FormularioArea } from '@/types/formulario';
import { toast } from 'sonner';

export function useFormularioAreas(formularioId: string) {
  const queryClient = useQueryClient();

  // Fetch áreas
  const { data: areas = [], isLoading } = useQuery({
    queryKey: ['formulario-areas', formularioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formulario_areas')
        .select('*')
        .eq('formulario_id', formularioId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (error) throw error;
      return data as FormularioArea[];
    },
    enabled: !!formularioId,
  });

  // Add área
  const addArea = useMutation({
    mutationFn: async (novaArea: Omit<FormularioArea, 'id' | 'criado_em' | 'atualizado_em'>) => {
      const { data, error } = await supabase
        .from('formulario_areas')
        .insert(novaArea)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario-areas', formularioId] });
      toast.success('Área adicionada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao adicionar área:', error);
      toast.error('Erro ao adicionar área');
    },
  });

  // Update área
  const updateArea = useMutation({
    mutationFn: async (area: Partial<FormularioArea> & { id: string }) => {
      const { error } = await supabase
        .from('formulario_areas')
        .update(area)
        .eq('id', area.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario-areas', formularioId] });
      toast.success('Área atualizada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao atualizar área:', error);
      toast.error('Erro ao atualizar área');
    },
  });

  // Delete área (soft delete)
  const deleteArea = useMutation({
    mutationFn: async (areaId: string) => {
      // Primeiro, desassociar campos desta área
      await supabase
        .from('formulario_campos')
        .update({ area_id: null })
        .eq('area_id', areaId);

      // Depois, desativar a área
      const { error } = await supabase
        .from('formulario_areas')
        .update({ ativo: false })
        .eq('id', areaId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario-areas', formularioId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-campos', formularioId] });
      toast.success('Área removida com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao remover área:', error);
      toast.error('Erro ao remover área');
    },
  });

  // Reorder áreas
  const reorderAreas = useMutation({
    mutationFn: async (updates: { id: string; ordem: number }[]) => {
      const promises = updates.map(({ id, ordem }) =>
        supabase
          .from('formulario_areas')
          .update({ ordem })
          .eq('id', id)
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) throw errors[0].error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formulario-areas', formularioId] });
      toast.success('Ordem das áreas atualizada!');
    },
    onError: (error) => {
      console.error('Erro ao reordenar áreas:', error);
      toast.error('Erro ao reordenar áreas');
    },
  });

  return {
    areas,
    isLoading,
    addArea: addArea.mutate,
    updateArea: updateArea.mutate,
    deleteArea: deleteArea.mutate,
    reorderAreas: reorderAreas.mutate,
    isAdding: addArea.isPending,
    isUpdating: updateArea.isPending,
    isDeleting: deleteArea.isPending,
  };
}
